import { useCallback, useEffect, useRef, useState } from 'react';
import { primeCaptainAudioOnGesture, requestCaptainAlertPermission } from '../../utils/captainAlerts';
import {
  applyCaptainSpeechUtterance,
  getCaptainSpeechVoices,
  pickCaptainSpeechVoice,
  resetCaptainSpeechVoiceCache,
} from '../../utils/captainSpeechVoice';

export interface CaptainTTSOptions {
  /** Server-persisted preference; when set, overrides local default */
  persistedEnabled?: boolean | null;
  /** Called when user toggles voice; persist to backend here */
  onPersist?: (enabled: boolean) => void | Promise<void>;
}

function resumeSpeechSynthesis(): void {
  if (typeof window === 'undefined' || !window.speechSynthesis) return;
  try {
    if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
    }
  } catch {
    // Safari may throw if resume is unsupported in current state
  }
}

function resolveCaptainVoice(
  cachedVoices: SpeechSynthesisVoice[]
): SpeechSynthesisVoice | undefined {
  const voices = cachedVoices.length ? cachedVoices : getCaptainSpeechVoices();
  return pickCaptainSpeechVoice(voices);
}

export function useCaptainTTS(options?: CaptainTTSOptions) {
  const [voiceEnabled, setVoiceEnabledState] = useState(false);
  const [selectedVoiceName, setSelectedVoiceName] = useState<string | null>(null);
  const speakingRef = useRef(false);
  const queueRef = useRef<string[]>([]);
  const hydratedRef = useRef(false);
  const unlockedRef = useRef(false);
  const voicesRef = useRef<SpeechSynthesisVoice[]>([]);
  const processQueueRef = useRef<() => void>(() => {});

  const refreshVoices = useCallback(() => {
    resetCaptainSpeechVoiceCache();
    voicesRef.current = getCaptainSpeechVoices();
    const voice = pickCaptainSpeechVoice(voicesRef.current);
    setSelectedVoiceName(voice?.name ?? null);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return undefined;

    const onVisible = () => {
      if (!document.hidden) {
        resumeSpeechSynthesis();
        processQueueRef.current();
      }
    };

    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return undefined;

    refreshVoices();
    window.speechSynthesis.addEventListener('voiceschanged', refreshVoices);
    return () => window.speechSynthesis.removeEventListener('voiceschanged', refreshVoices);
  }, [refreshVoices]);

  useEffect(() => {
    if (options?.persistedEnabled == null) {
      return;
    }
    setVoiceEnabledState(options.persistedEnabled);
    hydratedRef.current = true;
  }, [options?.persistedEnabled]);

  const speakUnlockUtterance = useCallback(() => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;

    const synth = window.speechSynthesis;
    resumeSpeechSynthesis();

    const utterance = new SpeechSynthesisUtterance('\u200B');
    utterance.volume = 0.01;
    const voice = resolveCaptainVoice(voicesRef.current);
    applyCaptainSpeechUtterance(utterance, voice);
    utterance.onend = () => {
      unlockedRef.current = true;
    };
    utterance.onerror = () => {
      unlockedRef.current = true;
    };
    synth.speak(utterance);
  }, []);

  const unlockVoice = useCallback(() => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;

    primeCaptainAudioOnGesture();
    const synth = window.speechSynthesis;
    resumeSpeechSynthesis();

    const voices = getCaptainSpeechVoices();
    if (!voices.length) {
      const onVoicesChanged = () => {
        synth.removeEventListener('voiceschanged', onVoicesChanged);
        refreshVoices();
        speakUnlockUtterance();
        processQueueRef.current();
      };
      synth.addEventListener('voiceschanged', onVoicesChanged);
      return;
    }

    refreshVoices();
    speakUnlockUtterance();
    processQueueRef.current();
  }, [refreshVoices, speakUnlockUtterance]);

  const setVoiceEnabled = useCallback(
    (enabled: boolean) => {
      setVoiceEnabledState(enabled);
      if (enabled) {
        unlockVoice();
        void requestCaptainAlertPermission();
      } else {
        unlockedRef.current = false;
      }
      if (options?.onPersist) {
        Promise.resolve(options.onPersist(enabled)).catch(() => {
          // preference save failed; UI stays optimistic until next refresh
        });
      }
      if (!enabled && typeof window !== 'undefined' && window.speechSynthesis) {
        window.speechSynthesis.cancel();
        queueRef.current = [];
        speakingRef.current = false;
      }
    },
    [options?.onPersist, unlockVoice]
  );

  const processQueue = useCallback(() => {
    if (speakingRef.current || !voiceEnabled) return;
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;

    const next = queueRef.current.shift();
    if (!next) return;

    resumeSpeechSynthesis();

    speakingRef.current = true;
    const utterance = new SpeechSynthesisUtterance(next);
    const voice = resolveCaptainVoice(voicesRef.current);
    applyCaptainSpeechUtterance(utterance, voice);

    const finish = () => {
      speakingRef.current = false;
      processQueueRef.current();
    };

    const timeout = window.setTimeout(finish, 20000);

    utterance.onend = () => {
      window.clearTimeout(timeout);
      unlockedRef.current = true;
      finish();
    };
    utterance.onerror = () => {
      window.clearTimeout(timeout);
      if (!unlockedRef.current) {
        queueRef.current.unshift(next);
      }
      finish();
    };

    window.speechSynthesis.speak(utterance);

    // Safari iOS sometimes stays paused until resume is called after speak()
    resumeSpeechSynthesis();
  }, [voiceEnabled]);

  processQueueRef.current = processQueue;

  const speak = useCallback(
    (text: string) => {
      if (!text.trim() || !voiceEnabled) return;
      if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;
      queueRef.current.push(text);
      processQueue();
    },
    [voiceEnabled, processQueue]
  );

  const cancel = useCallback(() => {
    queueRef.current = [];
    speakingRef.current = false;
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  }, []);

  useEffect(() => () => cancel(), [cancel]);

  const isAvailable =
    typeof window !== 'undefined' && 'speechSynthesis' in window;

  return {
    voiceEnabled,
    setVoiceEnabled,
    unlockVoice,
    speak,
    cancel,
    isAvailable,
    selectedVoiceName,
    preferenceHydrated: hydratedRef.current || options?.persistedEnabled != null,
  };
}
